import { useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useLazyGetSourcingCampaignQuery } from 'services/sourcing';
import { updateJobSourcingCampaign } from 'hooks/useJobs/actions';

const POLLING_INTERVAL = 5000; // 5 seconds
const POLLING_TIMEOUT = 300000; // 5 minutes

export const useCampaignPolling = () => {
  const dispatch = useDispatch();
  const [getSourcingCampaign] = useLazyGetSourcingCampaignQuery();
  const activePollingRef = useRef({});

  const pollCampaign = useCallback(
    async (campaignId) => {
      const currentEntry = activePollingRef.current[campaignId];

      // Campaign stopped elsewhere
      if (!currentEntry) {
        return;
      }

      // Stop if timeout exceeded
      if (Date.now() - currentEntry.startTime > POLLING_TIMEOUT) {
        if (currentEntry.intervalId) {
          clearInterval(currentEntry.intervalId);
        }
        delete activePollingRef.current[campaignId];
        return;
      }

      try {
        const campaign = await getSourcingCampaign({ id: campaignId }).unwrap();

        if (campaign?.status) {
          dispatch(
            updateJobSourcingCampaign({
              jobId: currentEntry.jobId,
              sourcingCampaign: campaign,
            }),
          );

          // Stop polling if status is no longer "calibrating"
          if (campaign.status !== 'calibrating') {
            if (currentEntry.intervalId) {
              clearInterval(currentEntry.intervalId);
            }
            delete activePollingRef.current[campaignId];
          }
        }
      } catch (error) {
        // If campaign not found or error, stop polling for this campaign
        if (currentEntry.intervalId) {
          clearInterval(currentEntry.intervalId);
        }
        delete activePollingRef.current[campaignId];
      }
    },
    [dispatch, getSourcingCampaign],
  );

  const startPolling = useCallback(
    (campaignId, jobId) => {
      if (!campaignId || !jobId) return () => {};

      // Don't start if already polling this campaign
      if (activePollingRef.current[campaignId]) {
        return () => {
          const entry = activePollingRef.current[campaignId];
          if (entry?.intervalId) {
            clearInterval(entry.intervalId);
          }
          delete activePollingRef.current[campaignId];
        };
      }

      const startTime = Date.now();
      const entry = {
        jobId,
        startTime,
        intervalId: null,
      };

      // Start polling immediately, then on interval
      activePollingRef.current[campaignId] = entry;
      pollCampaign(campaignId);
      entry.intervalId = setInterval(() => pollCampaign(campaignId), POLLING_INTERVAL);

      // Return stop function
      return () => {
        const stopEntry = activePollingRef.current[campaignId];
        if (stopEntry?.intervalId) {
          clearInterval(stopEntry.intervalId);
        }
        delete activePollingRef.current[campaignId];
      };
    },
    [pollCampaign],
  );

  return startPolling;
};
